"use client";

import * as React from "react";

import { Check, ChevronDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shadcn/ui/command";
import { Drawer, DrawerContent, DrawerTrigger } from "@/shadcn/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/ui/popover";

import { cn } from "@/components/common/utils";
import { useMediaQuery } from "@/components/common/hooks/useMediaQuery";

interface ComboboxOption {
  shortLabel?: string;
  label: string;
  value: string;
}

interface ComboboxProps {
  value: string;
  label: string;
  setValue: (value: string) => void;
  options: ComboboxOption[];
  variant?: "minimal" | "normal";
}

export function ComboBoxResponsive({
  value,
  setValue,
  label,
  options,
  variant = "normal",
}: ComboboxProps) {
  const [open, setOpen] = React.useState<boolean>(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const showPlaceholder = value === "";

  const activeOption = options.find((option) => option.value === value);

  const buttonLabel = showPlaceholder
    ? `Select your ${label}...`
    : activeOption?.shortLabel || activeOption?.label;

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            role="combobox"
            aria-controls="popover-content"
            aria-expanded={open}
            className={cn(
              "bg-white flex flex-row justify-between items-center w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px] text-left hover:bg-neutral-50 active:bg-neutral-100",
              variant === "minimal" ? "px-3 py-2.5" : "px-4 py-3",
              showPlaceholder && "text-neutral-400"
            )}
          >
            <span className="line-clamp-1">{buttonLabel}</span>
            <ChevronDown className="w-6 h-6 ml-1 -mr-1" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] p-0 z-[99] h-[320px] font-regular"
          align="start"
          id="popover-content"
        >
          <OptionsList
            options={options}
            label={label}
            value={value}
            setValue={setValue}
            setOpen={setOpen}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          className={cn(
            "bg-white flex flex-row justify-between items-center w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px] text-left hover:bg-neutral-50 active:bg-neutral-100",
            variant === "minimal" ? "px-3 py-2.5" : "px-4 py-3",
            showPlaceholder && "text-neutral-400"
          )}
        >
          <span className="line-clamp-1">{buttonLabel}</span>
          <ChevronDown className="w-6 h-6 ml-1 -mr-1" />
        </button>
      </DrawerTrigger>
      <DrawerContent className="z-[99] font-regular">
        <div className="mt-4 border-t">
          <OptionsList
            options={options}
            label={label}
            value={value}
            setValue={setValue}
            setOpen={setOpen}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface OptionsListProps {
  label: string;
  options: ComboboxOption[];
  value: string;
  setValue: (value: string) => void;
  setOpen: (value: boolean) => void;
}

function OptionsList({
  options,
  label,
  value,
  setValue,
  setOpen,
}: OptionsListProps) {
  return (
    <Command>
      <CommandInput placeholder={`Search for a ${label}...`} autoFocus />
      <div className="h-[75dvh] overflow-y-scroll">
        <CommandEmpty>No {label} found.</CommandEmpty>
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.value}
              value={option.value}
              onSelect={(currentValue) => {
                setValue(currentValue === value ? "" : currentValue);
                setOpen(false);
              }}
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  value === option.value ? "opacity-100" : "opacity-0"
                )}
              />
              {option.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </div>
    </Command>
  );
}
